import { Skeleton } from "@/components/ui/Skeleton";
import { Link } from "@/i18n/Link";
import type { ProductCard as ProductCardType } from "@/lib/centra";
import { formatPrice } from "@/lib/utils";
import { clsx } from "@frend-digital/ui";
import type { ComponentProps } from "react";
import styles from "./ProductCard.module.css";
import {
	ProductCardLink,
	ProductCardMedia,
	ProductCardPrice,
	ProductCardTags,
	ProductProvider,
} from "./client";
import { VariantDot } from "./client/Variants";
import { getProductLink } from "./util";

export interface CampaignTag {
	name: string;
	color?: string | number;
}

export interface ProductCardProps extends ComponentProps<"article"> {
	product: ProductCardType;
	sizes: string;
	priority?: boolean;
	parentHref?: string;
	showRatings?: boolean;
	campaignTags?: CampaignTag[];
	displayVariants?: boolean;
	itemListName?: string;
	itemListId?: string;
}

export const ProductCard = ({
	product,
	sizes,
	priority,
	parentHref,
	displayVariants = true,
	showRatings,
	campaignTags = [],
	itemListName,
	itemListId,
	...rest
}: ProductCardProps) => {
	const link = getProductLink(parentHref, product);
	const variants = product.variants.slice(0, 4);
	const variantsLeft = Math.max(0, product.variants.length - 4);
	const mobileVariantsLeft = Math.max(0, product.variants.length - 3);
	const outOfStock = product.items?.every((item) => item.stock === "no");

	const tags: Array<{
		text: string;
		className?: string;
		outlined?: boolean;
		filled?: boolean;
		color?: string | number;
	}> = [];

	if (outOfStock) {
		tags.push({ text: "Sold out", className: styles.soldOut });
	} else {
		/* 	if (product.showAsOnSale) {
			tags.push({ text: "Sale", className: styles.sale });
		} */
		if (product.showAsNew) {
			tags.push({ text: "New", className: styles.new });
		}

		campaignTags.forEach((campaignTag) => {
			if (campaignTag.color) {
				tags.push({
					text: campaignTag.name,
					filled: true,
					color: campaignTag.color,
				});
			}
		});
	}
	const tagLabels = tags.map((tag) => tag.text);

	// Get the first campaign tag color for price styling
	const campaignTagColor = campaignTags.find((tag) => tag.color)?.color;

	const variantMedia = Object.fromEntries(product.variants.map((v) => [v.displayItemId, v.media]));
	return (
		<ProductProvider defaultVariant={product.displayItemId}>
			<ProductCardLink
				data-animate
				{...rest}
				defaultVariant={product.displayItemId}
				href={link}
				collectionPath={parentHref}
				productId={product.displayItemId}
				depictListingResultId={product.depictListingResultId}
				depictSearchResultId={product.depictSearchResultId}
				depictProductId={product.depictProductId}
				itemListName={itemListName}
				itemListId={itemListId}
				productSku={product.sku}
				productName={product.name}
				productPrice={product.priceAsNumber}
			>
				<div data-fade>
					<ProductCardMedia
						media={product.media}
						href={link}
						sizes={sizes}
						priority={priority}
						variantMedia={variantMedia}
						product={product}
						tagLabels={tagLabels}
					>
						{outOfStock && <div className={styles.outOfStockOverlay} />}
						<ProductCardTags tags={tags} />
					</ProductCardMedia>

					<section>
						<dl className={styles.info}>
							<dt className="sr-only">Product name</dt>
							<dd className={styles.name}>
								<Link href={link} aria-label={`${product.name} - ${tagLabels.join(", ")}`}>
									{product.name}
								</Link>
							</dd>
							<dt className="sr-only">Price</dt>
							<ProductCardPrice
								price={formatPrice(product.price)}
								showAsOnSale={!!product.showAsOnSale}
								priceBeforeDiscount={formatPrice(product.priceBeforeDiscount)}
								campaignTagColor={campaignTagColor}
							/>
						</dl>
						{(displayVariants || showRatings) && (
							<div className={styles.variantsAndRating}>
								{displayVariants && (
									<div className={styles.variantDots}>
										{/* Desktop: show 4 variants */}
										<div className={styles.desktopVariants}>
											{variants.map((v) => (
												<VariantDot
													key={v.displayItemId + v.variantName}
													variant={v}
													parentHref={parentHref}
												/>
											))}
											{variantsLeft > 0 && (
												<span className={styles.additional}>+{variantsLeft}</span>
											)}
										</div>
										{/* Mobile: show 3 variants */}
										<div className={styles.mobileVariants}>
											{product.variants.slice(0, 3).map((v) => (
												<VariantDot
													key={v.displayItemId + v.variantName}
													variant={v}
													parentHref={parentHref}
												/>
											))}
											{mobileVariantsLeft > 0 && (
												<span className={styles.additional}>+{mobileVariantsLeft}</span>
											)}
										</div>
									</div>
								)}
								{showRatings && (
									<div className={styles.rating}>
										<span
											className="lipscore-rating-small"
											data-ls-load
											data-ls-product-name={product.name}
											data-ls-brand="Viking"
											data-ls-product-id={product.centraProductNumber}
											data-ls-variant-id={product.displayItemId}
											data-ls-variant-name={product.variantName}
											data-ls-sku={product.sku}
											data-ls-description={product.shortDescription}
											data-ls-image-url={product.media.at(0)?.href}
											data-ls-price={product.priceAsNumber}
											data-ls-price-currency={product.price?.match(/[A-Z]{3}$/)?.[0]}
											data-ls-category={product.canonicalUri}
										/>
									</div>
								)}
							</div>
						)}
					</section>
				</div>
			</ProductCardLink>
		</ProductProvider>
	);
};

export const ProductCardSkeleton = () => {
	return (
		<article data-animate className={styles.root} style={{ paddingBottom: "1em" }}>
			<div>
				<Skeleton className={styles.image} />
				<section>
					<dl className={styles.info}>
						<dt className="sr-only">Product name</dt>
						<dd className={styles.name}>
							<Skeleton scaleY fit>
								<span>Loading product name</span>
							</Skeleton>
						</dd>
						<dt className="sr-only">Price</dt>
						<dd className={styles.price}>
							<Skeleton scaleY fit>
								<span>0.00 NOK</span>
							</Skeleton>
						</dd>
					</dl>
					<div className={styles.variantDots}>
						<Skeleton scaleY scaleX fit>
							<span className={clsx(styles.variantDot)} />
						</Skeleton>
						<Skeleton scaleY scaleX fit>
							<span className={clsx(styles.variantDot)} />
						</Skeleton>
					</div>
				</section>
			</div>
		</article>
	);
};
